import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

interface FormPageNavigatorProps {
  currentPage: number | string;
  totalPages: number;
  basePath?: string; // e.g., '/form/page'
  targetFormRef: React.RefObject<HTMLDivElement>;
  zoomScale: number; // To re-trigger position calculation when zoom changes
  disabled?: boolean; // Grey out navigation when eligibility criteria not met
  onNavigate?: (targetPath: string) => void; // Optional callback to intercept navigation (e.g., for save modal)
}

const NavigatorContainer = styled.div<{
  dynamicLeft?: string;
  dynamicRight?: string;
  disabled?: boolean;
}>`
  position: fixed;
  top: 50%;
  left: ${props => props.dynamicLeft || 'auto'};
  right: ${props => props.dynamicRight || '20px'}; /* Default position */
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  padding: 15px 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001; // Ensure it's above other content like FloatingBackButton
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const NavItem = styled.div<{
  isActive: boolean;
  isClickable: boolean;
}>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => (props.isActive ? '#4A6CF7' : '#e0e0e0')};
  color: ${props => (props.isActive ? 'white' : '#333')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
  font-weight: bold;
  cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  transition: all 0.3s ease;
  border: 2px solid ${props => (props.isActive ? '#4A6CF7' : 'transparent')};

  &:hover {
    background-color: ${props => (props.isClickable && !props.isActive ? '#c0c0c0' : '')};
    transform: ${props => (props.isClickable ? 'scale(1.1)' : '')};
  }
`;



const FormPageNavigator: React.FC<FormPageNavigatorProps> = ({ currentPage, totalPages, basePath = '/form/page', targetFormRef, zoomScale, disabled = false, onNavigate }) => {
  const [styleProps, setStyleProps] = React.useState<{ dynamicLeft?: string; dynamicRight?: string }>({ dynamicRight: '20px' });
  const NAVIGATOR_ESTIMATED_WIDTH = 50; // px, approx width of the navigator (30px item + 2*10px padding)



  React.useEffect(() => {
    const calculatePosition = () => {
      if (targetFormRef.current) {
        const formRect = targetFormRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Desired position: 5px to the right of the form's right edge
        let newLeft = formRect.right + 20;

        // If placing it there makes it go off-screen or too close to the viewport edge, fallback
        if (newLeft + NAVIGATOR_ESTIMATED_WIDTH > viewportWidth - 20) { // Keep 20px from viewport edge
          setStyleProps({ dynamicLeft: 'auto', dynamicRight: '20px' });
        } else {
          setStyleProps({ dynamicLeft: `${newLeft}px`, dynamicRight: 'auto' });
        }
      } else {
        // Fallback if no targetRef, position 20px from right viewport edge (initial default)
        setStyleProps({ dynamicLeft: 'auto', dynamicRight: '20px' });
      }
    };

    calculatePosition(); // Initial calculation

    window.addEventListener('resize', calculatePosition);
    // The effect will re-run if zoomScale changes, triggering re-calculation.

    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [targetFormRef, zoomScale, targetFormRef.current]); // Ensure re-calc if ref.current becomes available or zoom changes

  const navigate = useNavigate();

  const handleNavigate = (routePath: number | string) => {
    const targetPath = `${basePath}${routePath}`;
    // Scroll to top when navigating between pages
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (onNavigate) {
      onNavigate(targetPath); // Let parent handle navigation (e.g., show save modal)
    } else {
      navigate(targetPath);
    }
  };

  // Build array of {displayLabel, routePath} for sequential display with actual routes
  // Pages 1-3, then A, then 4, then 14 (displayed as 5)
  const pageItems: Array<{ displayLabel: string; routePath: number | string }> = [
    { displayLabel: '1', routePath: 1 },
    { displayLabel: '2', routePath: 2 },
    { displayLabel: '3', routePath: 3 },
    { displayLabel: 'A', routePath: 'A' },
    { displayLabel: '4', routePath: 4 },
    { displayLabel: '5', routePath: totalPages }
  ];

  return (
    <NavigatorContainer dynamicLeft={styleProps.dynamicLeft} dynamicRight={styleProps.dynamicRight} disabled={disabled}>
      {pageItems.map(item => (
        <NavItem
          key={String(item.routePath)}
          isActive={item.routePath === currentPage}
          isClickable={item.routePath !== currentPage && !disabled}
          onClick={() => !disabled && handleNavigate(item.routePath)}
          title={disabled ? 'Navigation disabled - eligibility criteria not met' : `Page ${item.displayLabel}`}
        >
          {item.displayLabel}
        </NavItem>
      ))}
    </NavigatorContainer>
  );
};

export default FormPageNavigator;
